import type { TextareaHTMLAttributes, Ref } from 'react';
import { useState, useId } from 'react';
import type { TextareaVariant, TextareaSize, TextareaResize } from './Textarea.css';
import {
  container,
  containerFullWidth,
  label as labelClass,
  labelUnderline,
  wrapper as wrapperClass,
  wrapperFullWidth,
  textareaRecipe,
  errorMessage as errorMessageClass,
} from './Textarea.css';

export type { TextareaVariant, TextareaSize, TextareaResize };

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  ref?: Ref<HTMLTextAreaElement>;
  variant?: TextareaVariant;
  textareaSize?: TextareaSize;
  resize?: TextareaResize;
  fullWidth?: boolean;
  error?: string;
  label?: string;
  className?: string;
}

export function Textarea({
  variant = 'default',
  textareaSize = 'md',
  resize = 'vertical',
  fullWidth = false,
  disabled = false,
  error,
  label,
  className,
  style,
  onFocus,
  onBlur,
  id: externalId,
  ref,
  ...props
}: TextareaProps) {
  const autoId = useId();
  const errorId = useId();
  const textareaId = externalId ?? autoId;
  const [isFocused, setIsFocused] = useState(false);
  const effectiveVariant = error ? 'error' : variant;

  const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    if (!disabled) setIsFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    setIsFocused(false);
    onBlur?.(e);
  };

  const textareaClass = textareaRecipe({
    textareaSize,
    variant: effectiveVariant,
    isFocused: isFocused && !disabled && !error,
    isDisabled: disabled,
    resize,
  });

  return (
    <div className={`${container}${fullWidth ? ` ${containerFullWidth}` : ''}`}>
      {label && (
        <label htmlFor={textareaId} className={labelClass}>
          {label}
          <span className={labelUnderline} />
        </label>
      )}
      <div className={`${wrapperClass}${fullWidth ? ` ${wrapperFullWidth}` : ''}`}>
        <textarea
          ref={ref}
          id={textareaId}
          disabled={disabled}
          className={`${textareaClass}${className ? ` ${className}` : ''}`}
          style={style}
          onFocus={handleFocus}
          onBlur={handleBlur}
          aria-invalid={!!error}
          aria-describedby={error ? errorId : undefined}
          {...props}
        />
      </div>
      {error && (
        <span id={errorId} role="alert" className={errorMessageClass}>
          {error}
        </span>
      )}
    </div>
  );
}
